import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { IoChevronForward } from "react-icons/io5";
import Main from "../../components/layout/Main";
import { useToggleTop } from "../../components/common/ToggleTop";
import useAlive from "../../hooks/useAlive";
import { UserService } from "../../services/UserService";
import NewRemoteDialog from "./NewRemoteDialog";
import { BRANDS_PER_PAGE, REMOTE_PATHS, asList } from "./remoteUtils";
import "../../assets/styles/app-remotes.css";

// The app's Choose Brand (app/screens/HashhRemote/ChooseBrand.js): the
// category's brands from the IRDB, 15 at a time, with a search that runs half
// a second after typing stops, and the app's "Cannot find your Remote/Brand?"
// to make a remote by hand. Location state: { blaster, category, steps }.
function ChooseBrand() {
  const { state } = useLocation();
  const history = useHistory();
  const toast = useToggleTop();
  const alive = useAlive();
  const blaster = state?.blaster;
  const category = state?.category;
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");
  const [brands, setBrands] = useState([]);
  // "loading", "ready" or "error" for the first page.
  const [status, setStatus] = useState("loading");
  // Whether a full page came back, so more may follow.
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [creating, setCreating] = useState(false);
  // Only the latest request may fill the list, so typing can't mix results.
  const latest = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 500);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(
    async (from) => {
      if (!category) return;
      const request = ++latest.current;
      setLoadingMore(from > 0);
      if (from === 0) setStatus("loading");
      let page = null;
      try {
        page = asList(await UserService.getRemoteBrands(category.id, encodeURIComponent(term), from, BRANDS_PER_PAGE));
      } catch (error) {
        page = null;
      }
      if (!alive.current || request !== latest.current) return;
      setLoadingMore(false);
      if (!page) {
        if (from === 0) setStatus("error");
        else toast("Couldn't load more brands. Please try again.", "red");
        return;
      }
      setBrands((previous) => (from === 0 ? page : [...previous, ...page]));
      setMore(page.length === BRANDS_PER_PAGE);
      setStatus("ready");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [category, term]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  if (!blaster || !category) return <Redirect to={REMOTE_PATHS.UNIVERSAL} />;

  const choose = (brand) => history.push(REMOTE_PATHS.ADD_TEST, { blaster, category, brand, steps: state.steps + 1 });

  let list;
  if (status === "loading") {
    list = (
      <div className="hx-remote-brand-list" aria-label="Loading">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="hx-skel hx-remote-brand-skel" />
        ))}
      </div>
    );
  } else if (status === "error") {
    list = (
      <div className="hx-empty" role="alert">
        Couldn't load the brands.
        <button type="button" className="hx-btn" onClick={() => load(0)}>
          Try again
        </button>
      </div>
    );
  } else if (brands.length) {
    list = (
      <ul className="hx-list hx-remote-brand-list">
        {brands.map((brand) => (
          <li key={brand.id}>
            <button type="button" className="hx-list-item" onClick={() => choose(brand)}>
              <span className="hx-list-text">
                <span className="hx-list-title">{brand.name_en}</span>
              </span>
              <IoChevronForward className="hx-list-arrow" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
    );
  } else {
    list = <p className="hx-remote-none">{term ? `No brands match "${term}".` : "No brands in this category yet."}</p>;
  }

  return (
    <Main header="stack" title="Choose Brand" hideTabBar>
      <div className="hx-remote-brands">
        <input
          type="search"
          className="hx-input"
          placeholder="Search for brands..."
          aria-label="Search for brands"
          value={search}
          autoComplete="off"
          onChange={(event) => setSearch(event.target.value)}
        />
        {list}
        {status === "ready" && more ? (
          <button type="button" className="hx-btn is-ghost hx-remote-more" disabled={loadingMore} onClick={() => load(brands.length)}>
            {loadingMore ? "Loading…" : "More brands"}
          </button>
        ) : null}
        {status !== "loading" ? (
          <button type="button" className="hx-remote-missing" onClick={() => setCreating(true)}>
            Cannot find your Remote/Brand? Click here!
          </button>
        ) : null}
      </div>
      <NewRemoteDialog
        open={creating}
        blaster={blaster}
        category={category}
        onClose={() => setCreating(false)}
        onAdded={() => history.go(-state.steps)}
      />
    </Main>
  );
}

export default ChooseBrand;
